import { useCallback, useMemo, useRef, useState } from "react"
import { enqueueSnackbar } from "notistack"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router-dom"
import { faCopy, faFileExport } from "@fortawesome/pro-light-svg-icons"
import { useQueryClient } from "@tanstack/react-query"

import { renderRowActionMenuItems } from "../components/tableElements/renderRowActionMenuItems"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import {
    fetchProducts,
    MerchantProductsQuery,
    moveOrCopyToPriceList,
    MoveOrCopyToPriceListBody,
    updateStock,
} from "../productsApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useMerchant } from "../../../../merchants/merchantHooks"
import {
    defaultPreviewProductImageState,
    DefaultPreviewProductImageStateType,
    PreviewProductImage,
} from "../components/tableElements/PreviewProductImage"
import {
    defaultConfirmModalState,
    DefaultConfirmModalStateType,
    DeleteProductDialog,
} from "../components/tableElements/DeleteProductDialog"
import { getProductRowId, reorderProductRows, transformProductsDataWithSortOrder } from "../helpers"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import {
    MRT_ColumnDef,
    MRT_Row,
    MRT_RowSelectionState,
    type MRT_SortingState,
    type MRT_TableInstance,
} from "material-react-table"
import {
    ProductItem,
    ProductStockInfo,
    ProductTableRow,
    ProductTableRowType,
    UpdateStockPayload,
} from "../productsTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { ProductGroupItemBox, ProductName } from "../ProductTableColumns"
import { StockPopover } from "../components/stockManagement/StockPopover"
import { useExportProducts } from "../hooks/useExportProducts"
import { useOptimisticOrder } from "../hooks/useOptimisticOrder"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { ActionBarItem, FloatingActionBar } from "../../../../../shared/components/table/FloatingActionBar"
import { MoveOrCopyToPriceListDialog } from "../components/MoveOrCopyToPriceListDialog"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { EnvironmentType } from "../../../../environment/envTypes"
import { PriceListQuery, sortProductInPriceList } from "../../priceList/priceListApi"
import { EnableProductSort } from "../../priceList/components/EnableProductSort"
import { getEditProductGroupRoute, getEditProductRoute } from "../../../../../constants/routes"
import { deleteProductGroup, ProductGroupQuery } from "../../productGroup/productGroupApi"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../../shared/components/table/useGridLayoutStyles"

const useStyles = makeStyles()((theme) => ({
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
    actionBarItem: {
        width: "fit-content",
    },
    productRowCell: {
        // `!important` so the product indent (nesting under its group) wins over MRT's
        // grid-mode cell padding, which otherwise overrides it via emotion ordering.
        paddingLeft: "2.5rem !important",
    },
    topToolbar: {
        minHeight: "4rem",
        alignItems: "flex-start",
    },
    // Stack the "Enable product sort" control under the price-list dropdown inside the toolbar.
    toolbarStack: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: ".75rem",
        paddingBottom: ".5rem",
    },
    // Reveal the drag handle (Move) only when hovering the row, so the Move
    // column reads as part of the adjacent select-checkbox control group.
    bodyRow: {
        "& .product-drag-cell .MuiButtonBase-root": {
            opacity: 0,
            transition: "opacity .15s",
        },
        "&:hover .product-drag-cell .MuiButtonBase-root": {
            opacity: 1,
        },
    },
    // Group rows are headers, not reorderable on this page — never show the drag handle.
    groupRowNoDrag: {
        "& .product-drag-cell .MuiButtonBase-root": {
            display: "none",
        },
    },
}))

type StockPopoverState = Readonly<{
    anchorEl: HTMLElement | null
    productId: number | null
    stock: ProductStockInfo | null
}>

const defaultStockPopoverState: StockPopoverState = {
    anchorEl: null,
    productId: null,
    stock: null,
}

type MoveOrCopyDialogState = Readonly<{ open: false } | { open: true; type: "move" | "copy" }>

type DeleteGroupDialogState = Readonly<{
    open: boolean
    groupId: number | null
    groupName: string
}>

const defaultDeleteGroupDialogState: DeleteGroupDialogState = {
    open: false,
    groupId: null,
    groupName: "",
}

function isRowSelectable(row: MRT_Row<ProductTableRow>): boolean {
    return row.original.type === ProductTableRowType.PRODUCT
}

const DISPLAY_COLUMN_DEF_OPTIONS = {
    "mrt-row-select": {
        size: 45,
        maxSize: 45,
    },
    // Pin the Move column tight against the checkbox (no side padding) and tag the
    // cell so the row-hover style can reveal the drag handle. Header stays empty.
    "mrt-row-drag": {
        header: "",
        size: COLUMN_SIZE.XS,
        maxSize: COLUMN_SIZE.XS,
        muiTableBodyCellProps: {
            align: "center" as const,
            className: "product-drag-cell",
            style: { paddingLeft: 0, paddingRight: 0 },
        },
    },
} as const

const TABLE_INITIAL_STATE = { density: "spacious" } as const

type AllProductsTabProps = Readonly<{
    priceListId: number | "NONE"
    isResetProductSort: boolean
    toolbarLeftContent?: React.ReactNode
}>

export function AllProductsTab({ priceListId, isResetProductSort, toolbarLeftContent }: AllProductsTabProps) {
    const { merchantId } = useMerchant()
    const { classes, cx } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const queryClient = useQueryClient()

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)

    const [previewImage, setPreviewImage] = useState<DefaultPreviewProductImageStateType>(
        defaultPreviewProductImageState,
    )

    const [stockPopoverState, setStockPopoverState] = useState<StockPopoverState>(defaultStockPopoverState)

    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [globalFilter, setGlobalFilter] = useState<string>("")
    const [showGlobalFilter, setShowGlobalFilter] = useState(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [moveOrCopyDialog, setMoveOrCopyDialog] = useState<MoveOrCopyDialogState>({ open: false })
    const [isActionLoading, setIsActionLoading] = useState(false)
    const [deleteGroupDialog, setDeleteGroupDialog] = useState<DeleteGroupDialogState>(defaultDeleteGroupDialogState)

    const allowActionBar = !useMatchEnvironment([EnvironmentType.Prod])

    // Stable per-day value so the query key / dependencies object stays referentially
    // stable across renders (avoids per-render query churn and keeps `select` memoized).
    const deliveryDate = useMemo(() => dateTimeFactory.toDate(dateTimeFactory.now()), [dateTimeFactory])

    const productsDependencies = useMemo(
        () => ({ merchantId, priceListId, deliveryDate }),
        [merchantId, priceListId, deliveryDate],
    )

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: productsDependencies,
        select: transformProductsDataWithSortOrder,
    })

    // Optimistic local ordering so a drag shows instantly; rolls back on failure.
    const { orderedRows, applyOptimisticMove, rollback } = useOptimisticOrder(products.data, reorderProductRows)

    const sortPositionMaps = useMemo(() => {
        const productPositionMap = new Map<number, number>()
        const groupCounters = new Map<number | undefined, number>()

        for (const item of orderedRows) {
            if (item.type === ProductTableRowType.PRODUCT) {
                const count = (groupCounters.get(item.groupId) ?? 0) + 1
                groupCounters.set(item.groupId, count)
                productPositionMap.set(item.id, count)
            }
        }

        return { productPositionMap }
    }, [orderedRows])

    const { mutateAsync: callUpdateStock } = useApiMutation(updateStock, {
        async onSuccess(_params, _data, context) {
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("product-stock-update-success"),
            })
            // to prevent popover shifting on table refresh
            setTimeout(() => {
                context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            }, 300)
        },
    })

    const { mutateAsync: callMoveOrCopyToPriceList } = useApiMutation(moveOrCopyToPriceList, {})

    const { mutateAsync: callDeleteProductGroup } = useApiMutation(deleteProductGroup, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const allowSort = !useMatchEnvironment([EnvironmentType.Prod]) && isResetProductSort
    const canSort = allowSort && typeof priceListId === "number"

    // Sort is applied optimistically on drop. On failure: roll back to the pre-move
    // order, then re-throw so the API error is surfaced as a snackbar.
    const { mutate: callSortProduct } = useApiMutation(sortProductInPriceList, {
        async onError(_, error) {
            rollback()
            throw error
        },
    })

    const handleStockChipClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, productId: number) => {
            const productRow = orderedRows.find(
                (row) => row.type === ProductTableRowType.PRODUCT && row.id === productId,
            )
            if (productRow?.type === ProductTableRowType.PRODUCT && productRow.stock) {
                setStockPopoverState({
                    anchorEl: event.currentTarget,
                    productId,
                    stock: productRow.stock,
                })
            }
        },
        [orderedRows],
    )

    const handleStockUpdate = useCallback((payload: UpdateStockPayload) => callUpdateStock(payload), [callUpdateStock])

    const activeStockProductIdRef = useRef<number | null>(null)
    activeStockProductIdRef.current = stockPopoverState.productId

    const handleProductPhotoClick = useCallback((imageUrl: string) => {
        setPreviewImage({ show: true, image: imageUrl })
    }, [])

    const handleProductClick = useCallback(
        (productId: number) => {
            navigate(getEditProductRoute(productId))
        },
        [navigate],
    )

    const handleGroupClick = useCallback(
        (groupId: number) => {
            navigate(getEditProductGroupRoute(groupId))
        },
        [navigate],
    )

    const handleDeleteGroup = useCallback((groupId: number, groupName: string) => {
        setDeleteGroupDialog({ open: true, groupId, groupName })
    }, [])

    const exportProducts = useExportProducts({ data: orderedRows })

    const { ApiQueryTable, columnFactory } = useApiQueryTable({
        data: orderedRows,
        isInitialLoading: products.isInitialLoading,
        isFetching: products.isFetching,
        isError: products.isError,
    })

    const groupSelectionData = useMemo(() => {
        const productsByGroup = new Map<number, ProductItem[]>()
        const selectionStates = new Map<number, "none" | "some" | "all">()
        const selectedProducts: ProductItem[] = []

        for (const item of orderedRows) {
            if (item.type !== ProductTableRowType.PRODUCT) continue
            const groupId = item.groupId
            if (groupId !== undefined) {
                const list = productsByGroup.get(groupId)
                if (list) list.push(item)
                else productsByGroup.set(groupId, [item])
            }
            if (rowSelection[`product-${item.id}`]) {
                selectedProducts.push(item)
            }
        }

        for (const [groupId, items] of productsByGroup) {
            const selectedCount = items.filter((p) => rowSelection[`product-${p.id}`]).length
            if (selectedCount === 0) selectionStates.set(groupId, "none")
            else if (selectedCount === items.length) selectionStates.set(groupId, "all")
            else selectionStates.set(groupId, "some")
        }

        return { productsByGroup, selectionStates, selectedProducts }
    }, [orderedRows, rowSelection])

    const selectedProducts = groupSelectionData.selectedProducts

    const handleGroupSelectionToggle = useCallback(
        (groupId: number) => {
            const groupProducts = groupSelectionData.productsByGroup.get(groupId) ?? []
            const anySelected = groupSelectionData.selectionStates.get(groupId) !== "none"
            setRowSelection((prev) => {
                const next = { ...prev }
                for (const p of groupProducts) {
                    const key = `product-${p.id}`
                    if (anySelected) {
                        delete next[key]
                    } else {
                        next[key] = true
                    }
                }
                return next
            })
        },
        [groupSelectionData],
    )

    const handleMoveOrCopyConfirm = useCallback(
        async (targetPriceListId: number) => {
            const isCopy = moveOrCopyDialog.open && moveOrCopyDialog.type === "copy"
            setIsActionLoading(true)
            try {
                const body: MoveOrCopyToPriceListBody = {
                    productOperation: {
                        type: isCopy ? "COPY" : "MOVE",
                        destination: { priceList: { id: targetPriceListId } },
                        productIds: selectedProducts.map((p) => p.id),
                    },
                }
                const result = await callMoveOrCopyToPriceList(body)
                enqueueSnackbar({
                    variant: "success",
                    message: result.serviceResponse.displayMessage,
                })
                setRowSelection({})
                setMoveOrCopyDialog({ open: false })
                queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            } finally {
                setIsActionLoading(false)
            }
        },
        [selectedProducts, moveOrCopyDialog, callMoveOrCopyToPriceList, queryClient],
    )

    const handleDragEnd = useCallback(
        (table: MRT_TableInstance<ProductTableRow>) => {
            const { draggingRow, hoveredRow } = table.getState()
            if (!hoveredRow || !draggingRow || orderedRows.length === 0) return
            if (!("original" in hoveredRow)) return

            const draggingItem = draggingRow.original
            const targetItem = (hoveredRow as MRT_Row<ProductTableRow>).original
            const numericPriceListId = priceListId as number

            // Only products can be dragged; group headers stay put.
            if (draggingItem.type !== ProductTableRowType.PRODUCT) return

            // Show the new order immediately (snapshots internally for rollback).
            applyOptimisticMove(draggingItem, targetItem)

            if (draggingItem.type === ProductTableRowType.PRODUCT && targetItem.type === ProductTableRowType.PRODUCT) {
                const isSameGroup = draggingItem.groupId === targetItem.groupId
                const position = sortPositionMaps.productPositionMap.get(targetItem.id) ?? 1
                callSortProduct({
                    priceListId: numericPriceListId,
                    productId: draggingItem.id,
                    body: {
                        moveTo: {
                            ...(!isSameGroup &&
                                targetItem.groupId && {
                                    productGroup: { id: targetItem.groupId },
                                }),
                            position,
                        },
                    },
                })
            }

            if (draggingItem.type === ProductTableRowType.PRODUCT && targetItem.type === ProductTableRowType.GROUP) {
                callSortProduct({
                    priceListId: numericPriceListId,
                    productId: draggingItem.id,
                    body: {
                        moveTo: {
                            ...(draggingItem.groupId !== targetItem.id && {
                                productGroup: { id: targetItem.id },
                            }),
                            position: 1,
                        },
                    },
                })
            }
        },
        [orderedRows.length, priceListId, sortPositionMaps, applyOptimisticMove, callSortProduct],
    )

    const columns = useMemo<MRT_ColumnDef<ProductTableRow>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                header: l10n.getString("products-name-column"),
                size: COLUMN_SIZE.L,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: ({ row }) => ({
                    className: cx(
                        GROW_CELL_CLASS,
                        row.original.type === ProductTableRowType.PRODUCT && classes.productRowCell,
                    ),
                }),
                Cell: ({ row }) => {
                    if (row.original.type === ProductTableRowType.GROUP) {
                        return <ProductGroupItemBox productGroup={row.original} onGroupClick={handleGroupClick} />
                    }

                    if (row.original.type === ProductTableRowType.PRODUCT) {
                        return (
                            <ProductName
                                product={row.original}
                                onProductPhotoClick={handleProductPhotoClick}
                                onStockChipClick={handleStockChipClick}
                                activeStockProductIdRef={activeStockProductIdRef}
                                onProductClick={handleProductClick}
                            />
                        )
                    }

                    return ""
                },
            }),
            columnFactory.moneyColumn(
                {
                    id: "price",
                    header: l10n.getString("products-price-column"),
                    size: COLUMN_SIZE.SS,
                    maxSize: COLUMN_SIZE.SS,
                },
                (row) => (row.type === ProductTableRowType.GROUP ? undefined : row.price),
                true,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("products-status-column"),
                },
                (row) => row.isActive ?? false,
                {
                    // Groups render the same Active/Inactive chip as products once the API
                    // provides their status; stay blank only while it is still unknown.
                    cellOverride: (row) =>
                        row.type === ProductTableRowType.GROUP && row.isActive === undefined ? "" : null,
                },
            ),
        ],
        [
            l10n,
            columnFactory,
            cx,
            handleProductPhotoClick,
            handleStockChipClick,
            handleProductClick,
            handleGroupClick,
            classes.productRowCell,
        ],
    )

    // Only products can be dragged; group headers stay put.
    const dragHandleProps = useCallback(
        ({ table, row }: { table: MRT_TableInstance<ProductTableRow>; row: MRT_Row<ProductTableRow> }) => ({
            disabled: row.original.type === ProductTableRowType.GROUP,
            onDragEnd: () => handleDragEnd(table),
        }),
        [handleDragEnd],
    )

    const selectAllCheckboxProps = useCallback(
        ({ table }: { table: MRT_TableInstance<ProductTableRow> }) => ({
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (table.getIsSomeRowsSelected()) {
                    setRowSelection({})
                } else {
                    table.getToggleAllRowsSelectedHandler()(e)
                }
            },
        }),
        [],
    )

    const selectCheckboxProps = useCallback(
        ({ row }: { row: MRT_Row<ProductTableRow> }) => {
            if (row.original.type !== ProductTableRowType.GROUP) return {}
            const state = groupSelectionData.selectionStates.get(row.original.id) ?? "none"
            return {
                disabled: false,
                checked: state === "all",
                indeterminate: state === "some",
                onChange: () => handleGroupSelectionToggle(row.original.id),
            }
        },
        [groupSelectionData.selectionStates, handleGroupSelectionToggle],
    )

    const rowProps = useCallback(
        ({ row }: { row: MRT_Row<ProductTableRow> }) => ({
            className: cx(classes.bodyRow, row.original.type === ProductTableRowType.GROUP && classes.groupRowNoDrag),
        }),
        [cx, classes.bodyRow, classes.groupRowNoDrag],
    )

    const rowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<ProductTableRow>; closeMenu: () => void }) =>
            renderRowActionMenuItems({
                row,
                closeMenu,
                setConfirmModalState,
                navigate,
                errorClasses: classes.deleteItem,
                onDeleteGroup: handleDeleteGroup,
            }),
        [navigate, classes.deleteItem, handleDeleteGroup],
    )

    const toolbarInternalActions = useCallback(
        ({ table }: { table: MRT_TableInstance<ProductTableRow> }) => (
            <StandardTableFilter variant="right" table={table}>
                <StandardExportButton onClick={() => exportProducts()} />
            </StandardTableFilter>
        ),
        [exportProducts],
    )

    const enableSortAction =
        !isResetProductSort && typeof priceListId === "number" ? (
            <EnableProductSort
                priceListId={priceListId}
                onEnabled={() => {
                    queryClient.invalidateQueries({ queryKey: [PriceListQuery.LIST] })
                    queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
                }}
            />
        ) : null

    const topToolbarProps = useMemo(() => ({ className: classes.topToolbar }), [classes.topToolbar])

    return (
        <Grid container direction="column">
            <ApiQueryTable
                columns={columns}
                layoutMode="grid"
                muiTablePaperProps={{ className: gridClasses.pinned }}
                memoMode="cells"
                initialState={TABLE_INITIAL_STATE}
                state={{ sorting, globalFilter, showGlobalFilter, rowSelection }}
                onSortingChange={setSorting}
                onGlobalFilterChange={setGlobalFilter}
                onShowGlobalFilterChange={setShowGlobalFilter}
                onRowSelectionChange={setRowSelection}
                getRowId={getProductRowId}
                enableRowSelection={isRowSelectable}
                enableRowOrdering={canSort}
                muiTableBodyRowDragHandleProps={canSort ? dragHandleProps : undefined}
                muiSelectAllCheckboxProps={selectAllCheckboxProps}
                muiSelectCheckboxProps={selectCheckboxProps}
                displayColumnDefOptions={DISPLAY_COLUMN_DEF_OPTIONS}
                muiTopToolbarProps={topToolbarProps}
                renderTopToolbarCustomActions={() => (
                    <div className={classes.toolbarStack}>
                        {toolbarLeftContent}
                        {enableSortAction}
                    </div>
                )}
                renderToolbarInternalActions={toolbarInternalActions}
                renderRowActionMenuItems={rowActionMenuItems}
                muiTableBodyRowProps={rowProps}
            />

            {/* Floating action bar for selected products */}
            {allowActionBar && selectedProducts.length > 0 && (
                <FloatingActionBar
                    selectedItemsLength={selectedProducts.length}
                    handleCloseActionBar={() => setRowSelection({})}
                >
                    <ActionBarItem
                        containerStyle={classes.actionBarItem}
                        icon={faFileExport}
                        title={l10n.getString("products-action-bar-move-to-price-list")}
                        onClick={() => setMoveOrCopyDialog({ open: true, type: "move" })}
                    />
                    <ActionBarItem
                        containerStyle={classes.actionBarItem}
                        icon={faCopy}
                        title={l10n.getString("products-action-bar-copy-to-price-list")}
                        onClick={() => setMoveOrCopyDialog({ open: true, type: "copy" })}
                    />
                </FloatingActionBar>
            )}

            {/* Move / Copy dialog */}
            {moveOrCopyDialog.open && (
                <MoveOrCopyToPriceListDialog
                    open={moveOrCopyDialog.open}
                    onClose={() => setMoveOrCopyDialog({ open: false })}
                    type={moveOrCopyDialog.type}
                    selectedProducts={selectedProducts}
                    onConfirm={handleMoveOrCopyConfirm}
                    isLoading={isActionLoading}
                />
            )}

            {/* Confirm delete product */}
            {confirmModalState.showConfirmModal && (
                <DeleteProductDialog
                    confirmModalState={confirmModalState}
                    setConfirmModalState={setConfirmModalState}
                />
            )}

            {/* Confirm delete product group */}
            {deleteGroupDialog.open && deleteGroupDialog.groupId && (
                <DeleteConfirmDialog
                    open={deleteGroupDialog.open}
                    onClose={() => setDeleteGroupDialog(defaultDeleteGroupDialogState)}
                    typeOfEntity={l10n.getString("product-group-delete")}
                    entityName={deleteGroupDialog.groupName}
                    handleClick={() => {
                        if (deleteGroupDialog.groupId) {
                            callDeleteProductGroup({
                                productGroupId: deleteGroupDialog.groupId,
                                merchantId: merchantId ?? 0,
                            })
                        }
                        setDeleteGroupDialog(defaultDeleteGroupDialogState)
                    }}
                />
            )}

            {/* Product preview */}
            {previewImage.show && <PreviewProductImage previewImage={previewImage} setPreviewImage={setPreviewImage} />}

            {/* Stock popover */}
            {stockPopoverState.anchorEl && stockPopoverState.productId && stockPopoverState.stock && (
                <StockPopover
                    anchorEl={stockPopoverState.anchorEl}
                    onClose={() => setStockPopoverState(defaultStockPopoverState)}
                    productId={stockPopoverState.productId}
                    currentStock={stockPopoverState.stock}
                    onStockUpdate={handleStockUpdate}
                />
            )}
        </Grid>
    )
}
